import { RootState, useAppDispatch } from '_store';
import { devicesSelector } from '_store/devices/selectors';
import manageSpatemRsusFormReducer from '_store/forms/manage-spatem-rsus';
import { spatemByIdSelector } from '_store/spatem/selectors';
import { spatemsSagas } from '_store/spatem/spatemsSagas';
import { useLocalization } from '@fluent/react';
import { Button, CardContent, Typography } from '@material-ui/core';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useRolePermissions } from '../../../hooks/useRolePermissions';
import { RoleGuard } from '../../items/RoleGuard';
import RsuMultiSelect from '../../items/RsuMultiSelect';
import styles from '../Pmv/styles';
import { SpatemRsusManageType } from './types';

const SpatemRsusManage: FC<SpatemRsusManageType> = ({ id, closeDialog }) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const { canManageSpatem, blockedActionMode } = useRolePermissions();

  const spatem = useSelector(spatemByIdSelector(id));
  const devices = useSelector(devicesSelector);
  const formState = useSelector((state: RootState) => state.forms['manage-spatem-rsus']);
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  // Prefilled once per SPATEM: its payload changes with every message, which would overwrite the user's edits
  const spatemLoaded = Boolean(spatem);
  useEffect(() => {
    if (spatem) {
      dispatch(manageSpatemRsusFormReducer.actions.prefillForm(spatem));
    }
  }, [id, spatemLoaded, dispatch]);

  const rsuOptions = useMemo(
    () => devices.map((device) => ({ id: device.name, name: device.name })),
    [devices],
  );

  const onRsusChange = useCallback(
    (selectedIds: string[]) => {
      dispatch(manageSpatemRsusFormReducer.actions.rsusChanged(selectedIds));
    },
    [dispatch],
  );

  const submitForm = () => {
    dispatch(
      spatemsSagas.actions.updateSpatemRsus({
        id,
        identifier: formState.identifier,
        rsus: formState.rsus,
      }),
    );
    closeDialog();
  };

  if (!spatem) {
    return <>{l10n.getString('forms-spatem-not-found')}</>;
  }

  return (
    <CardContent className={classes.cardContent}>
      <div className={classes.rsuSelect}>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          {l10n.getString('forms-spatem-rsus')}
        </Typography>
        <RsuMultiSelect
          options={rsuOptions}
          selectedIds={formState.rsus}
          onChange={onRsusChange}
          placeholder={l10n.getString('forms-spatem-rsus-placeholder')}
        />
      </div>

      <RoleGuard
        allowed={canManageSpatem}
        blockedActionMode={blockedActionMode}
        blockedMessage={writeBlockedMessage}
      >
        <Button
          variant="outlined"
          className={classes.submitButton}
          disabled={formState.rsus.length === 0 || !formState.identifier}
          onClick={submitForm}
          fullWidth
        >
          {l10n.getString('forms-spatem-update-rsus')}
        </Button>
      </RoleGuard>
    </CardContent>
  );
};

export default React.memo(SpatemRsusManage);
